import axios from 'axios';
import { API_URL } from '../constants/api';
import { FieldNewsDto } from './fieldNewsService';
import { RefereeDto } from './refereeService';

// Создаём axios instance с автоматическим добавлением refereeToken
const refereeApiClient = axios.create({
  baseURL: API_URL,
});

refereeApiClient.interceptors.request.use((config) => {
  const refereeToken = localStorage.getItem('refereeToken');
  if (refereeToken) {
    config.headers.Authorization = `Bearer ${refereeToken}`;
  }
  return config;
});

// Field map response type
interface FieldMapResponse {
  id: string;
  fieldId: string;
  name: string;
  description: string;
  schemaImageUrl: string;
  schemaVideoUrl: string;
  images: string[];
  youtubeVideos: string[];
  nodes: any[];
}

// Get field ID for main referee
export const getMainRefereeFieldId = async (): Promise<{ fieldId: string }> => {
  const response = await refereeApiClient.get('/api/main-referee/field-id');
  return response.data;
};

// Maps payload type (same as fieldService)
interface FieldMapPayload {
  name: string;
  fieldId: string;
  description?: string;
  schemaVideoUrl?: string;
  youtubeVideos?: string[];
  devices?: any[];
}

// Maps
export const getMainRefereeMaps = async (): Promise<FieldMapResponse[]> => {
  const response = await refereeApiClient.get('/api/main-referee/maps');
  return response.data;
};

export const createMainRefereeMap = async (
  payload: FieldMapPayload,
  schemaImage?: File,
  images?: File[]
): Promise<FieldMapResponse> => {
  const formData = new FormData();
  formData.append('name', payload.name);
  formData.append('fieldId', payload.fieldId);
  formData.append('description', payload.description || '');

  if (payload.schemaVideoUrl) {
    formData.append('schemaVideoUrl', payload.schemaVideoUrl);
  }

  if (payload.youtubeVideos && payload.youtubeVideos.length > 0) {
    payload.youtubeVideos.forEach(url => {
      formData.append('youtubeVideos', url);
    });
  }

  if (schemaImage) {
    formData.append('schemaImage', schemaImage);
  }

  if (images && images.length > 0) {
    images.forEach(file => {
      formData.append('images', file);
    });
  }

  const response = await refereeApiClient.post('/api/main-referee/maps', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateMainRefereeMap = async (
  id: string,
  payload: FieldMapPayload,
  schemaImage?: File,
  newImages?: File[],
  imagesToDelete?: string[]
): Promise<FieldMapResponse> => {
  const formData = new FormData();
  formData.append('name', payload.name);
  formData.append('fieldId', payload.fieldId);
  formData.append('description', payload.description || '');

  if (payload.schemaVideoUrl) {
    formData.append('schemaVideoUrl', payload.schemaVideoUrl);
  }

  if (payload.youtubeVideos && payload.youtubeVideos.length > 0) {
    payload.youtubeVideos.forEach(url => {
      formData.append('youtubeVideos', url);
    });
  }

  if (schemaImage) {
    formData.append('schemaImage', schemaImage);
  }

  if (newImages && newImages.length > 0) {
    newImages.forEach(file => {
      formData.append('newImages', file);
    });
  }

  if (imagesToDelete && imagesToDelete.length > 0) {
    imagesToDelete.forEach(url => {
      formData.append('imagesToDelete', url);
    });
  }

  const response = await refereeApiClient.put(`/api/main-referee/maps/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteMainRefereeMap = async (mapId: string): Promise<void> => {
  await refereeApiClient.delete(`/api/main-referee/maps/${mapId}`);
};

// Field node response type
export interface FieldNodeResponse {
  id: string;
  saleNumber: string;
  nodeCode: string;
  nodeType: string;
  fieldId?: string;
  mapId?: string;
  position?: string;
  friendlyName: string;
  soldAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Field office node response type
export interface FieldOfficeNodeResponse {
  id: string;
  fieldId: string;
  fieldNodeId: string;
  name: string;
  description: string;
  saleNumber: string;
  nodeCode: string;
  nodeType: string;
  friendlyName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Nodes
export const getMainRefereeNodes = async (): Promise<FieldNodeResponse[]> => {
  const response = await refereeApiClient.get('/api/main-referee/nodes');
  return response.data;
};

export const getMainRefereeOfficeNode = async (): Promise<FieldOfficeNodeResponse | null> => {
  try {
    const response = await refereeApiClient.get('/api/main-referee/office-node');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// News
export const getMainRefereeNews = async (): Promise<FieldNewsDto[]> => {
  const response = await refereeApiClient.get('/api/main-referee/news');
  return response.data;
};

export const createMainRefereeNews = async (formData: FormData): Promise<FieldNewsDto> => {
  const response = await refereeApiClient.post('/api/main-referee/news', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateMainRefereeNews = async (newsId: string, formData: FormData): Promise<FieldNewsDto> => {
  const response = await refereeApiClient.put(`/api/main-referee/news/${newsId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteMainRefereeNews = async (newsId: string): Promise<void> => {
  await refereeApiClient.delete(`/api/main-referee/news/${newsId}`);
};

export const publishMainRefereeNews = async (newsId: string): Promise<void> => {
  await refereeApiClient.patch(`/api/main-referee/news/${newsId}/publish`);
};

export const unpublishMainRefereeNews = async (newsId: string): Promise<void> => {
  await refereeApiClient.patch(`/api/main-referee/news/${newsId}/unpublish`);
};

export const uploadMainRefereeNewsImage = async (imageFile: File): Promise<{ imageUrl: string }> => {
  const formData = new FormData();
  formData.append('imageFile', imageFile);
  const response = await refereeApiClient.post('/api/main-referee/news/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Referees
export const getMainRefereeReferees = async (): Promise<RefereeDto[]> => {
  const response = await refereeApiClient.get('/api/main-referee/referees');
  return response.data;
};

export const updateMainRefereeRefereeStatus = async (refereeId: string, status: string): Promise<void> => {
  await refereeApiClient.put(`/api/main-referee/referees/${refereeId}/status`, { status });
};

const mainRefereeService = {
  getFieldId: getMainRefereeFieldId,
  getMaps: getMainRefereeMaps,
  createMap: createMainRefereeMap,
  updateMap: updateMainRefereeMap,
  deleteMap: deleteMainRefereeMap,
  getNodes: getMainRefereeNodes,
  getOfficeNode: getMainRefereeOfficeNode,
  getNews: getMainRefereeNews,
  createNews: createMainRefereeNews,
  updateNews: updateMainRefereeNews,
  deleteNews: deleteMainRefereeNews,
  publishNews: publishMainRefereeNews,
  unpublishNews: unpublishMainRefereeNews,
  uploadNewsImage: uploadMainRefereeNewsImage,
  getReferees: getMainRefereeReferees,
  updateRefereeStatus: updateMainRefereeRefereeStatus
};

export default mainRefereeService;
